import { useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { useLang } from "../lib/publicI18n";

// Notice d'information RGPD (art. 13/14) pour le viewer public de proposition.
// Volontairement un overlay "maison" (comme ZoomableImage / SignatureDialog dans
// ce fichier) plutôt que le Dialog shadcn : PublicProposal.js n'importe pas
// components/ui/dialog et tout le reste du viewer public suit ce même patron
// (fixed inset-0 + backdrop). Pas de mur de consentement : simple lien + modale
// d'information, aucune action bloquante.
export default function PrivacyNotice({ companyName, trigger, className, testIdSuffix = "" }) {
  const [open, setOpen] = useState(false);
  const en = useLang() === "en";
  const name = companyName || (en ? "the issuer of this proposal" : "l'émetteur de cette proposition");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`privacy-notice-trigger${testIdSuffix}`}
        className={className || "underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors"}
      >
        {trigger || (en ? "Privacy" : "Confidentialité")}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-notice-title"
          data-testid="privacy-notice-modal"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 sm:p-6 animate-fade-up"
          >
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
                <h2 id="privacy-notice-title" className="font-heading text-lg font-bold leading-snug">
                  {en ? "Your data privacy" : "Confidentialité de vos données"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={en ? "Close" : "Fermer"}
                data-testid="privacy-notice-close"
                className="w-10 h-10 -mr-2 flex items-center justify-center rounded-full hover:bg-muted transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {en ? (
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">{name}</strong> is the controller of the personal data collected
                through this document, as the issuer of the proposal. The Acteio platform, published by{" "}
                <strong className="text-foreground">Cleya Développement</strong>, acts as a technical processor: it hosts
                and runs the tool, but does not use this data for any purpose of its own.
              </p>

              <p className="font-medium text-foreground">Data collected and purpose</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong className="text-foreground">Viewing the document</strong>: time of opening, IP address, reading
                  time per section, scroll depth. Legal basis: the legitimate interest of {name} in following the
                  progress of its proposal.
                </li>
                <li>
                  <strong className="text-foreground">If you sign</strong>: your name, the drawing of your signature, your IP
                  address, your browser and a fingerprint (hash) of the accepted document. Legal basis: steps taken at your
                  request prior to entering into a contract, and proof of acceptance.
                </li>
              </ul>

              <p>
                The email containing this proposal may include an open tracker (tracking pixel) letting {name} know
                whether it has been viewed.
              </p>

              <p>
                No card details are stored by the platform: any deposit is paid directly on Stripe's secure pages, and the
                direct debit mandate is signed on GoCardless's secure pages.
              </p>

              <p>
                Viewing data (including the IP address) is automatically anonymised after a period set by {name} (12 months
                by default); evidence relating to a signature is kept for the applicable statutory period.
              </p>

              <p>
                Under the GDPR, you have the right to access, rectify, erase and object to the processing of this data:
                send your request directly to <strong className="text-foreground">{name}</strong>, the controller. You may
                also lodge a complaint with the CNIL (cnil.fr), the French data protection authority, if you believe your
                rights are not being respected.
              </p>
            </div>
            ) : (
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">{name}</strong> est responsable du traitement des données
                personnelles collectées via ce document, en tant qu'émetteur de la proposition. La plateforme
                Acteio, éditée par <strong className="text-foreground">Cleya Développement</strong>, agit comme
                sous-traitant technique : elle héberge et fait fonctionner l'outil, mais n'utilise ces données
                pour aucune finalité qui lui soit propre.
              </p>

              <p className="font-medium text-foreground">Données collectées et finalité</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong className="text-foreground">Consultation du document</strong> : horodatage d'ouverture,
                  adresse IP, durée de lecture par section, profondeur de défilement. Base légale :
                  intérêt légitime de {name} à suivre l'avancement commercial de sa proposition.
                </li>
                <li>
                  <strong className="text-foreground">En cas de signature</strong> : votre nom, le tracé de votre
                  signature, votre adresse IP, votre navigateur et une empreinte (hash) du document accepté. Base
                  légale : exécution de mesures précontractuelles à votre demande et constitution d'une preuve
                  d'acceptation.
                </li>
              </ul>

              <p>
                L'email contenant cette proposition peut inclure un témoin d'ouverture (pixel de suivi) permettant
                à {name} de savoir s'il a été consulté.
              </p>

              <p>
                Aucune donnée de carte bancaire n'est stockée par la plateforme : un éventuel acompte est payé
                directement sur les pages sécurisées de Stripe, et le mandat de prélèvement est signé sur celles de
                GoCardless.
              </p>

              <p>
                Les données de consultation (dont l'adresse IP) sont anonymisées automatiquement à l'issue
                d'une durée définie par {name} (12 mois par défaut) ; les éléments de preuve liés à une
                signature sont conservés pendant la durée légale applicable.
              </p>

              <p>
                Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement et
                d'opposition sur ces données : adressez votre demande directement à{" "}
                <strong className="text-foreground">{name}</strong>, responsable du traitement. Vous pouvez
                également introduire une réclamation auprès de la CNIL (cnil.fr) si vous estimez que vos droits
                ne sont pas respectés.
              </p>
            </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
